import React, { useCallback } from 'react';
import { formatCurrency } from 'utils';
import debounce from 'lodash/debounce';
import { Controller, useFormContext } from 'react-hook-form';
import { Box, Typography, TextField } from '@mui/material';
// import { LenderProfile } from '@trnsact/trnsact-shared-types';
import { CurrencyInputField, SelectField, DateField } from 'components/form';
import { CreditLineFormValues } from '../model';
import { generateCreditLineLog } from '../lib';
import { creditStatusValues } from '../constants';
import { DateFormats } from 'constants/common';
import {
  CreditLineRequest,
  UpdateCreditLineRequestInput,
  CreateCreditLineRequestInput,
  CreditLineStatusEnum,
} from '@trnsact/trnsact-shared-types';
import { CreditLineNotes } from './CreditLineNotes';

interface Props {
  userName: string;
  requestedAmount: number;
  approvedAmount: number | null;
  status: CreditLineStatusEnum;
  onCreditLineUpdate: (data: UpdateCreditLineRequestInput) => Promise<void>;
  onCreditLineCreate: (data: CreateCreditLineRequestInput) => Promise<void>;
  creditLineRequest?: CreditLineRequest;
}

export const CreditLineForm = ({
  status,
  userName,
  requestedAmount,
  approvedAmount,
  onCreditLineUpdate,
  onCreditLineCreate,
  creditLineRequest,
}: Props) => {
  const { setValue, control, getValues } = useFormContext<CreditLineFormValues>();

  // --- Line Requested / Requested Line (amount) ---

  const debouncedUpdateAmount = useCallback(
    debounce(async (nextAmountValue: number) => {
      await onCreditLineUpdate({
        creditLineRequestId: creditLineRequest?.creditLineRequestId || '',
        approvedAmount: nextAmountValue,
      });
    }, 500),
    [onCreditLineUpdate]
  );

  const handleChangeAmount = async (nextAmountValue: number) => {
    setValue('approvedAmount', nextAmountValue);
    await debouncedUpdateAmount(nextAmountValue);
  };

  // --- Status ---

  const handleChangeStatus = async (nextStatusValue: CreditLineStatusEnum) => {
    setValue('status', nextStatusValue);

    const logMessage = generateCreditLineLog({
      userName,
      currentStatus: status,
      nextStatus: nextStatusValue,
      prevNotes: getValues('allNotes'),
    });

    setValue('allNotes', logMessage);

    await onCreditLineUpdate({
      creditLineRequestId: creditLineRequest?.creditLineRequestId || '',
      status: nextStatusValue as any,
    });
  };

  const debouncedUpdateNotes = useCallback(
    debounce(async (nextNotesValue: string) => {
      await onCreditLineUpdate({
        creditLineRequestId: creditLineRequest?.creditLineRequestId || '',
        notes: nextNotesValue,
      });
    }, 500),
    [onCreditLineUpdate]
  );

  const handleNotesChange = async (newNotes: string) => {
    setValue('notes', newNotes);
    await debouncedUpdateNotes(newNotes);
  };

  // --- Layout ---

  return (
    <Box className="lineSection">
      <Typography component="span" variant="subtitle2">
        Line Requested {formatCurrency(requestedAmount, true)}
      </Typography>

      <CurrencyInputField
        name="approvedAmount"
        control={control}
        label="Approved Line"
        customOnChange={handleChangeAmount}
        textFieldProps={{ fullWidth: true }}
      />

      <SelectField
        label="Status"
        control={control}
        name="status"
        options={creditStatusValues}
        extraAction={async nextValue => {
          await handleChangeStatus(nextValue as CreditLineStatusEnum);
        }}
      />

      <DateField
        control={control}
        label="Expiration Date"
        format={DateFormats.Short}
        name="expirationDate"
        slotProps={{ textField: { fullWidth: true } }}
        extraAction={async nextDate => {
          await onCreditLineUpdate({
            creditLineRequestId: creditLineRequest?.creditLineRequestId || '',
            expirationDate: nextDate?.toISOString(),
          });
        }}
      />

      <CreditLineNotes isLoading={false} onCreditLineNotesUpdate={handleNotesChange} />
    </Box>
  );
};
